// The call window's React root, chosen in main.tsx before App renders: /call-window/<call id> or
// /call-window/pending?launch=<nonce>. It runs CallWindowHost (its own session, config, call, event stream and the
// coach and transcript pollers) and renders CallWindowView. It never mounts the workspace, the shell or the phone,
// so it can never take the phone; it remembers its bounds, follows the theme the main window picks, and shows an
// ended call in place (it never closes itself).
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { capability } from '../../lib/capabilities.ts';
import { applyTheme, getThemePreference } from '../../lib/theme.ts';
import { APP_NAME } from '../../config/brand.ts';
import { coachVisible } from '../../call/coach/coach.ts';
import { browserOpener, cardExpected, contextCardModel, popOut } from '../phone/contextCard.ts';
import { type CallWindowTarget, writeCallWindowBounds } from './callWindowLaunch.ts';
import { CallWindowHost, type CallWindowSnapshot, DISPOSITION_FAILED, initialCallWindowSnapshot } from './callWindowHost.ts';
import {
  askOffReason, crmOpenStatus, crmPaneModel, dispositionStatus, effectiveState, firstName, headState, headerModel, lastCallerLine, pendingLine,
  sayNextModel, timerText, transcriptRows, transcriptStateWord, wrapUpShown,
} from './callWindowModel.ts';
import { CallWindowView, type CallWindowViewProps, type Vote } from './CallWindowView.tsx';
import { answerFeedbackTargetId, summaryFeedbackTargetId, tipFeedbackTargetId } from './feedbackClient.ts';
import { wrapUpQuietSkip } from './WrapUp.tsx';

const THEME_KEY = 'sidetone.theme';
const BOUNDS_SAVE_MS = 400;
const BOUNDS_POLL_MS = 2000;

export interface CallWindowUi {
  now: number;
  moreOpen: boolean;
  crmBlocked: boolean;
  pageHost: string | null;
}

/** A vote the host's snapshot mirrors from its CoachFeedbackVotes (callWindowHost.ts), by `${target}:${target_id}`. */
const voteOf = (s: CallWindowSnapshot, target: 'tip' | 'answer' | 'summary', id: string | null): Vote | null => (id ? s.votes[`${target}:${id}`] ?? null : null);

/** Everything the view shows, from the host's snapshot and the window's own UI state. */
export function callWindowViewProps(
  s: CallWindowSnapshot,
  ui: CallWindowUi,
  handlers: Pick<CallWindowViewProps, 'onAsk' | 'onMore' | 'onOpenCrm'> & Partial<Pick<CallWindowViewProps, 'onDisposition' | 'onTipVote' | 'onSummaryVote' | 'onAnswerVote'>>,
): CallWindowViewProps {
  const self = s.session?.user?.id ?? null;
  const call = s.phase === 'signed_out' ? null : s.call;
  // Signed out: nothing from the call stays on screen (the CRM card and transcript are the call's data).
  const own = call && s.card ? contextCardModel(s.card) : null;
  const previous = call && !own && s.previous?.card ? { model: contextCardModel(s.previous.card), fromCallAt: s.previous.fromCallAt } : null;
  const expected = !!call && cardExpected(call, capability(s.config, 'crm.sync'));
  const crm = crmPaneModel(own, previous, expected, ui.now);
  const header = headerModel(call, own ?? previous?.model ?? null);
  const state = headState(s.phase, call, self);
  const callState = effectiveState(call, self);
  const perms = s.session?.permissions ?? [];
  const coachAvailable = coachVisible(s.config?.capabilities['transcript.coach'] ?? null, perms, s.mediaReady);
  const say = sayNextModel({
    phase: s.phase === 'moved' ? 'ready' : s.phase,
    callState,
    coachAvailable,
    coachEnabled: s.coachEnabled,
    coach: s.phase === 'moved' ? { result: null, error: null } : s.coach,
    frozen: s.frozen,
    pin: s.pin,
    askStatus: s.askStatus,
    now: ui.now,
  });
  if (s.phase === 'moved') { say.status = 'Showing in a newer call window'; say.statusTone = 'quiet'; say.off = !say.line1; }
  if (s.phase === 'failed_start' && s.failMessage) say.status = 'The call didn’t start';
  const transcript = s.phase === 'signed_out' ? null : s.transcript.transcript;
  const participants = call?.participants ?? [];
  // The prospect by the card's first name in the transcript, except on a number the CRM says is a front desk or does not
  // know: whoever answers there (the receptionist) is not the contact, so it stays "Caller", as say-next's own row. A
  // card that reports no number kind (an older CRM release) keeps the name.
  const rawCard = own ? s.card : previous ? s.previous?.card ?? null : null;
  const sharedLine = rawCard?.slots?.number_kind === 'front_desk' || rawCard?.slots?.number_kind === 'unknown';
  const callerName = sharedLine ? null : firstName((own ?? previous?.model ?? null)?.who?.contactName);
  const rows = transcriptRows(transcript, participants, self, { callerName, displayed: s.displayed });
  const offHours = s.phase === 'ready' ? s.coach.offHours ?? null : null;
  // A vote goes to the auto-tip on screen: not a pinned quick answer, not the frozen lines on hold.
  const tipId = say.line1 && !s.pin && !say.muted ? tipFeedbackTargetId(s.coach.result) : null;
  const wrap = s.wrapup.callId !== null && s.wrapup.callId === call?.id ? s.wrapup : null;
  // The wrap-up takes say-next's box only once it exists or the server says it is coming (written, or being written).
  // When none is coming (Coach was off, no transcript grant, wrap-ups not set up, it never arrived) say-next stays,
  // saying "Call ended", instead of a box of "No ..." lines (review round 2). A skipped call (under 30 seconds, nothing
  // transcribed) has no wrap-up either, so say-next stays for it too. Once the box has said a wrap-up is coming it keeps
  // the place for the rest of the call's ending (wrapupHold), whatever the reads say next (review round 3).
  const held = !!wrap && s.wrapupHold?.callId === wrap.callId;
  const stalled = held && !!s.wrapupHold?.stalled && !wrap?.waiting && !wrap?.summary;
  const showWrapUp = wrapUpShown(s.phase, call) && !!wrap && (held || wrap.waiting || (wrap.summary !== null && !wrapUpQuietSkip(wrap.summary)));
  // Asking needs the coach: the box, its chips and the quick actions are off while it is not available (review round 2).
  const canAsk = s.phase === 'ready' && callState === 'active' && !offHours && coachAvailable;
  const answerVotes: Record<string, Vote> = {};
  for (const e of s.askEntries) { const v = voteOf(s, 'answer', answerFeedbackTargetId(e.id)); if (v) answerVotes[e.id] = v; }
  return {
    idBase: s.target.kind === 'call' ? s.target.callId : 'pending',
    phase: s.phase,
    head: { name: header.name, sub: header.sub, state: state.word, tone: state.tone, timer: timerText(call, ui.now) },
    say,
    caller: lastCallerLine(transcript, participants, s.displayed),
    askBusy: s.askBusy,
    canAsk,
    // A reason only while asking is off: the thread never shows one beside a working box.
    askOff: canAsk ? null : askOffReason({ phase: s.phase, callState, coachAvailable, offHours, now: ui.now }),
    callId: call?.id ?? null,
    transcript: { word: transcriptStateWord(call, s.stream === 'live'), rows, pending: pendingLine(rows) },
    tip: { targetId: tipId, voted: voteOf(s, 'tip', tipId) },
    wrapup: { show: showWrapUp, summary: showWrapUp ? wrap?.summary ?? null : null, waiting: showWrapUp && (!!wrap?.waiting || stalled), ...(showWrapUp && wrap?.until ? { until: wrap.until } : {}), ...(showWrapUp && stalled ? { stalled: true } : {}) },
    summaryVote: showWrapUp ? voteOf(s, 'summary', wrapUpVoteId(s)) : null,
    answerVotes,
    crm,
    // The card the pane's 4b slots read: the call's own, else the last call's with this number (none signed out).
    crmCard: rawCard,
    now: ui.now,
    crmOpenStatus: crmOpenStatus(crm.primary, ui.crmBlocked, ui.pageHost),
    dispo: {
      selected: s.disposition.selected,
      busy: s.disposition.busy,
      status: dispositionStatus(crm, s.disposition.status),
      warn: s.disposition.status === DISPOSITION_FAILED,
    },
    more: { open: ui.moreOpen && s.phase === 'ready' && callState === 'active', entries: s.askEntries },
    onDisposition: () => undefined,
    ...handlers,
  };
}

/** The summary vote's id for the snapshot's wrap-up (feedbackClient.ts summaryFeedbackTargetId), for the host's vote
 * call. Only a written wrap-up is rated: not while it is written, not a skipped call. */
export function wrapUpVoteId(s: CallWindowSnapshot): string | null {
  const summary = s.wrapup.callId && s.wrapup.callId === s.call?.id ? s.wrapup.summary : null;
  return summary && summary.skipped_reason === null ? summaryFeedbackTargetId(summary) : null;
}

function storage(): Storage | null {
  try { return typeof window === 'undefined' ? null : window.localStorage; } catch { return null; }
}

/** Saves the window's place on resize, on leaving, and every 2 s while visible (no event fires on a move). */
function useBoundsSaver(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let last = '';
    let timer: ReturnType<typeof setTimeout> | null = null;
    const save = () => {
      const bounds = { left: window.screenX, top: window.screenY, width: window.innerWidth, height: window.innerHeight };
      if (![bounds.left, bounds.top, bounds.width, bounds.height].every(Number.isFinite) || bounds.width < 100 || bounds.height < 100) return;
      const key = JSON.stringify(bounds);
      if (key === last) return;
      last = key;
      writeCallWindowBounds(storage(), bounds);
    };
    const onResize = () => { if (timer !== null) clearTimeout(timer); timer = setTimeout(save, BOUNDS_SAVE_MS); };
    window.addEventListener('resize', onResize);
    window.addEventListener('pagehide', save);
    const poll = setInterval(() => { if (document.visibilityState === 'visible') save(); }, BOUNDS_POLL_MS);
    return () => {
      if (timer !== null) clearTimeout(timer);
      clearInterval(poll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pagehide', save);
    };
  }, []);
}

const noSubscribe = () => () => undefined;

export function CallWindowRoot({ target }: { target: CallWindowTarget }) {
  const [host, setHost] = useState<CallWindowHost | null>(null);
  // Created inside the effect, so React StrictMode's development mount/unmount/mount starts one host and stops it again
  // cleanly instead of sharing a stopped one.
  useEffect(() => {
    const h = new CallWindowHost({ target });
    setHost(h);
    h.start();
    return () => { h.stop(); };
  }, [target]);
  const boot = useMemo(() => initialCallWindowSnapshot(target), [target]);
  const snapshot = useSyncExternalStore(host?.subscribe ?? noSubscribe, host?.getSnapshot ?? (() => boot), () => boot);
  const [now, setNow] = useState(() => Date.now());
  const [moreOpen, setMoreOpen] = useState(false);
  const [crmBlocked, setCrmBlocked] = useState(false);
  const callId = snapshot.call?.id ?? null;

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  // A new call starts with the sheet closed (the question thread clears its own draft).
  useEffect(() => { setMoreOpen(false); setCrmBlocked(false); }, [callId]);
  // The sheet only opens on a click while the call is connected: Hold, the end of the call or anything else that stops
  // the call being live closes it for good, so Resume never brings it back (and its focus) by itself.
  const connected = snapshot.phase === 'ready' && effectiveState(snapshot.call, snapshot.session?.user?.id ?? null) === 'active';
  useEffect(() => { if (!connected) setMoreOpen(false); }, [connected]);
  useBoundsSaver();
  // The main window changed the theme: follow it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => { if (e.key === THEME_KEY || e.key === null) applyTheme(getThemePreference()); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const props = callWindowViewProps(snapshot, {
    now, moreOpen, crmBlocked,
    pageHost: typeof window !== 'undefined' ? window.location?.hostname ?? null : null,
  }, {
    onAsk: question => { host?.ask(question); },
    onMore: open => setMoreOpen(open && connected),
    onOpenCrm: () => {
      const primary = props.crm.primary;
      if (primary) setCrmBlocked(popOut(primary.href, browserOpener(), true) === 'blocked');
    },
    onDisposition: id => { host?.recordDisposition(id); },
    onTipVote: vote => { host?.vote('tip', props.tip.targetId, vote); },
    onSummaryVote: vote => { host?.vote('summary', wrapUpVoteId(snapshot), vote); },
    onAnswerVote: (entryId, vote) => { host?.vote('answer', answerFeedbackTargetId(entryId), vote); },
  });

  useEffect(() => {
    document.title = `${props.head.name || 'Call'} - Call window - ${APP_NAME}`;
  }, [props.head.name]);

  return <CallWindowView {...props} />;
}
